import { Key, RowSelectMethod } from 'antd/es/table/interface';
import type { FixedType } from 'rc-table/lib/interface';
import { ReactNode, useRef } from 'react';
import { FetchPolicy, GraphQLTaggedNode, useFragment, useLazyLoadQuery, Variables } from 'react-relay';

import type { TasksQuery$variables } from '../graphql/__generated__/TasksQuery.graphql';
import {
    ActionsType,
    CustomTableActionType,
    ExtractKeys,
    FilterTableFieldParamType,
    ItemTaskType,
    ItemType,
    TableQueryType,
} from '../types';

export interface IGraphQLLoader<T extends TasksQuery$variables, E> {
    query: GraphQLTaggedNode;
    fragmentList: GraphQLTaggedNode;
    fragmentConnection: GraphQLTaggedNode;
    fragment: GraphQLTaggedNode | GraphQLTaggedNode[];
    mutationUpdate?: GraphQLTaggedNode;
    mutationCreate?: GraphQLTaggedNode;
    mutationDelete?: GraphQLTaggedNode;
    nameQuery: string;
    varRequest?: T;
    onUpdateData?: (rows: any[], count: number, connectionId: string, columns?: any[]) => void;
    prefixFetchKey?: string;
    prefixFetchKeyUpdate?: string;
    extendRow?: E[];
    extendRowByField?: E[][];
    fetchPolicy?: FetchPolicy;
    sizeOfPage?: string[];
    externalFetchKey?: number;
    needStructure?: boolean;
}

export interface IGraphQLTableLoader<T extends TasksQuery$variables, K, E> extends IGraphQLLoader<T, E> {
    // object to rewrite name for column.
    // Example: {name: 'Your Name', last_name: 'Your Last Name', age: ''}
    // Empty value means show column but use default label for column
    columnsLabel?: { [key in keyof K]: string };

    //list of field for filtering data
    // columnsFilter?: ExtractKeys<K>[];
    // paramFilter is improved version than previous columnsFilter
    // key is a name of field
    // value is object if object empty this means filter work as simple field with type
    paramFilter?: {
        [key in keyof Partial<K>]: FilterTableFieldParamType | {};
    };

    paramSorter?: ExtractKeys<K>[];
    renderDataColumn?: {
        [key in keyof Partial<K>]: <M extends K[key]>(value: M | any, record: any) => ReactNode;
    };
    // list of column with fixed position
    fixedColumn?: {
        [key in keyof Partial<K>]: FixedType;
    };
    // list of column for default hide
    defaultHiddenColumn?: string[];
    // list of allowed actions for table:
    //  - 'search' - show field search for highlight,
    //  - 'filter' - show button to control params for filtering,
    //  - 'sorter' - show sorting button for columns,
    //  - 'columnsView' - show button to control visible for columns,
    //  - 'export' - show button to export data table to PDF,
    //  - 'edit' - show button 'pencil' for each row of the table to edit open modal window for editing a row,
    //  - 'delete' - show button 'trash' for each row of the table to delete row from table,
    //  - 'createNew' - show button 'New record' to add new record to the table,
    actions?: ActionsType[];
    // custom action add button to column Action
    customActions?: CustomTableActionType[];
    customActionComponents?: ((item: ItemType | ItemTaskType, data: any) => ReactNode)[];
    externalFetchKey?: number;
    onClickRow?: (data: any, dataRaw: any) => void;
    heightTable?: number;
    onSelectionChange?: (
        selectedRowKeys: Key[],
        selectedRows: any[],
        info: {
            type: RowSelectMethod;
        },
    ) => void;
    onSelect?: (record: T, selected: boolean, selectedRows: T[], nativeEvent: Event) => void;
}

//ToDo need to improve approach removing data from raw GraphQL query
const _removeStructureFragmentFromQuery = (query: any) => {
    console.log(')_)_)_)_)_');
    const text = (query as any).params.text;
    // delete using fragment from query section
    let updatedText = text.replace(/structure\s*\{\s*\.\.\.StructureFragment\s*\}/s, '');
    // delete fragment
    updatedText = updatedText.replace(/fragment\s+StructureFragment\s+on\s+Structure.{475}/s, '');

    (query as any).params.text = updatedText;

    if ((query as any).operation.selections[0].selections.length === 3) {
        (query as any).operation.selections[0].selections = [
            JSON.parse(JSON.stringify((query as any).operation.selections[0].selections[0])),
            JSON.parse(JSON.stringify((query as any).operation.selections[0].selections[2])),
        ];
    }

    return query;
};

const useGraphQLLoader = <T extends TasksQuery$variables, E>({
    varRequest,
    query,
    fragmentList,
    fragmentConnection,
    nameQuery,
    prefixFetchKey,
    prefixFetchKeyUpdate,
    fetchPolicy,
}: IGraphQLLoader<T, E>) => {
    const dataPaginationAsString = varRequest ? `${nameQuery}-${varRequest.first}-${varRequest.page}` : '';

    const fetchKey = `${dataPaginationAsString}-${prefixFetchKey}-${prefixFetchKeyUpdate}`;

    const bufferRequestParams = useRef<{ [key: string]: string }>({});

    const getFetchPolicy = (prefixFetchKey?: string): FetchPolicy => {
        if (fetchPolicy) {
            return fetchPolicy;
        }

        if (prefixFetchKey) {
            return bufferRequestParams.current[dataPaginationAsString] === prefixFetchKey
                ? 'store-and-network'
                : 'network-only';
        }

        return 'store-or-network';
    };

    const dataStructureListFragment = useLazyLoadQuery(query, varRequest as Variables, {
        fetchPolicy: getFetchPolicy(prefixFetchKey),
        fetchKey,
    });

    bufferRequestParams.current[dataPaginationAsString] = prefixFetchKey || '';

    const dataStructureConnectionFragment = useFragment<any>(fragmentList, dataStructureListFragment);
    const connectionId = dataStructureConnectionFragment[nameQuery].__id;
    const dataConnection = useFragment<any>(
        fragmentConnection,
        dataStructureConnectionFragment[nameQuery],
    ) as TableQueryType;

    return { connectionId, dataConnection };
};

export default useGraphQLLoader;
